import { useState, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useGoCardlessDropin } from "@gocardless/react-dropin";
import { MobileNav } from "@/components/layout/mobile-nav";
import { useOnboardingChat } from "@/hooks/use-onboarding-chat";
import IDScanModal from "../pages/id-scan-modal";
import VerificationStartScreen from "../pages/verificationStartScreen";
import DocuSignModal from "../pages/docusign-modal";
import { ChatMessage } from "@/components/onboarding/ChatMessage";
import { ChatInput } from "@/components/onboarding/ChatInput";
import { OnboardingHeader } from "@/components/onboarding/OnboardingHeader";
import { OnboardingWarning } from "@/components/onboarding/OnboardingWarning";
import {
  VerificationStage,
  generateMessageId,
  createMessage,
} from "@/lib/onboardingUtils";
import { deriveOnboardingProgress } from "@/lib/onboardingProgress";
import { Card, CardContent } from "@/components/ui/card";
import NeedHelp from "@/components/NeedHelp";
import { getUserMessage } from "@/lib/errorMessages";
import SuccessPopup from "../pages/paymentSuccessPopup";
import checkUserSignatureCount from "@/features/onboarding/services/checkUserSignatureCount";
import {
  PREDEFINED_QA,
  suggestedTopics,
} from "@/features/onboarding/utils/constants";
import { startVerificationPolling } from "@/features/onboarding/services/verificationStatusPoller";

export default function OnboardingPage() {
  const { processUserMessage, isProcessing } = useOnboardingChat();
  const { user } = useAuth();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const runCountRef = useRef(0);

  // DocuSign states
  const [isDocuSignModalOpen, setIsDocuSignModalOpen] = useState(false);
  const [docuSignUrl, setDocuSignUrl] = useState("");
  const [isDocuSignLoading, setIsDocuSignLoading] = useState(false);
  const [signatureComplete, setSignatureComplete] = useState(false);
  const [envelopeId, setEnvelopeId] = useState("");
  const docuSignIframeRef = useRef(null);
  const [docuSignTimeout, setDocuSignTimeout] = useState(null);
  const [idScanTimeout, setIdScanTimeout] = useState(null);
  const [onFidoSrc, setOnFidoSrc] = useState();
  const [onFidoData, setOnfidoData] = useState();
  const [isResumeOnfidoFlow, setIsResumeOnfidoFlow] = useState(false);
  const [userWorkFlowRunData, setUserWorkFlowRunData] = useState("");

  // Payment states
  const [activeFlowId, setActiveFlowId] = useState(null);
  const [activeBillingRequestId, setActiveBillingRequestId] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState("");
    const [paymentEnvironment, setPaymentEnvironment] = useState("live");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCompletePaymentPopup, setShowCompletePaymentPopup] =
    useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [paymentSuccessFromUrl, setPaymentSuccessFromUrl] = useState<string | null>(null);
  // const [showSpinner, setShowSpinner] = useState(false);

  // Existing states...
  const [chatMessage, setChatMessage] = useState("");
  const [currentStage, setCurrentStage] = useState(VerificationStage.WELCOME);
  const [showIDScanModal, setShowIDScanModal] = useState(false);
  const [selectedIDType, setSelectedIDType] = useState(null);
  const [verificationResult, setVerificationResult] = useState(null);
  const [consentGiven, setConsentGiven] = useState(false);
  const [lastActivity, setLastActivity] = useState(new Date());

  const stageBeingProcessed = useRef(null);
  const [hasGeneratedSession, setHasGeneratedSession] = useState(false);

  // Form states (keeping existing ones)
  const [showBankingForm, setShowBankingForm] = useState(false);
  const [showModifyForm, setShowModifyForm] = useState(false);

  const [disablePrompts, setDisablePrompts] = useState(false);
  const [showVerificationScreen, setShowVerificationScreen] = useState(false);

  // Error states (keeping existing ones)
  const [messages, setMessages] = useState<any>([]);
  const [flowProcessed, setFlowProcessed] = useState(false);
  const [isManuallyFetching, setIsManuallyFetching] = useState(false);

  const userUsedAllAttemptsOfVerification =
    user.maxVerificationAttempts <= user.verificationAttempts;

  const [isMobile, setIsMobile] = useState(false);
  // Single query for tasks
  const {
    data: tasks,
    isLoading: tasksLoading,
    error: tasksError,
    refetch, // Add this
  } = useQuery({
    queryKey: ["/api/tasks"],
    enabled: !!user && !isManuallyFetching,
  });

  // Get payment task
  const paymentTask = tasks?.find(
    (task) => task.category === "payments" && task.status !== "completed"
  );

  // Whether any payment task is currently processing (server-driven)
  const isPaymentProcessing = !!(
    tasks &&
    tasks.some((t) => t.category === "payments" && t.status === "processing")
  );

  // Signature step is skipped for clients onboarded without DocuSign
  const signatureRequired = user?.docusignEnabled !== false;

  const workflowRunId =
    onFidoData && onFidoData.workflowRun.id
      ? onFidoData.workflowRun.id
      : user?.workFlowRunId;

  // =========================================
  // Step 3: Payment (GoCardless) — Helpers & Config
  // =========================================
  // Replace your entire GoCardless configuration with this:

  // Helper function to clear processing messages
  const clearProcessingMessages = () => {
    setShowPaymentModal(false);
    setMessages((prev) =>
      prev.filter(
        (msg) =>
          msg.content !== "Processing your payment... Please wait." &&
          msg.content !== "Checking your progress, Please wait..." &&
          msg.content !== "Please continue payment process on browser..." &&
          msg.content !== "⚙️ Setting up your payment... please wait." &&
          msg.content !== "We're processing your payment status... Please wait."
      )
    );
  };

  const hasTriggeredSuccess = useRef(false);

  // Step 3: Payment — GoCardless Drop-in configuration and callbacks
  const { open, exit, ready, error } = useGoCardlessDropin(
    activeFlowId
      ? {
          billingRequestFlowID: activeFlowId,
          environment: paymentEnvironment,
          onSuccess: async (billingRequest, billingRequestFlow) => {
            hasTriggeredSuccess.current = true;
            console.log("=== GOCARDLESS SUCCESS ===", {
              billingRequest,
              billingRequestFlow,
            });

            setDisablePrompts(true);
            setProcessingPayment(true);

            const currentBillingRequestId = billingRequest.id;
            setActiveBillingRequestId(currentBillingRequestId);

            // Show initial processing message
            const processingMessage = createMessage(
              "Processing your payment... Please wait.",
              false,
              { actionType: "info" }
            );
            addMessage(processingMessage);

            try {
              // Call fulfill API directly with fetch
              const res = await fetch("/api/gocardless/fulfill-payment", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  billingRequestId: currentBillingRequestId,
                  taskId: paymentTask?.id,
                }),
              });

              if (!res.ok) {
                throw new Error("Failed to complete payment");
              }

              const data = await res.json();
              console.log("✅ Fulfill API call successful", data);

              // Replace processing message with waiting message
              clearProcessingMessages();
              const waitMessage = createMessage(
                "Please wait a minute while we process your payment...",
                false,
                { actionType: "info" }
              );
              addMessage(waitMessage);

              // Give backend time to update before verifying
              setTimeout(async () => {
                console.log("Running check after onSuccess...");
                await checkPaymentFromTasks(waitMessage.id);
              }, 12000);
            } catch (err: any) {
              console.error("❌ Error in onSuccess:", err);

              clearProcessingMessages();
              const waitMessage = createMessage(
                "We are verifying your payment... Please wait.",
                false,
                { actionType: "info" }
              );
              addMessage(waitMessage);

              // Run the same delayed check
              setTimeout(async () => {
                console.log("Running fallback check after API error...");
                await checkPaymentFromTasks(waitMessage.id);
              }, 12000);
            } finally {
              setProcessingPayment(false);
              setActiveFlowId(null);

              try {
                exit();
              } catch (exitError) {
                console.log("Exit already called or modal closed");
              }
            }
          },

          onExit: async (exitError, metadata) => {
            setShowPaymentModal(false);
            setProcessingPayment(false);
            setActiveFlowId(null);

            console.log("=== GOCARDLESS EXIT ===", { exitError, metadata });

            if (hasTriggeredSuccess.current) {
              console.log(
                "✅ onSuccess already triggered. Skipping task refetch."
              );
              hasTriggeredSuccess.current = false;
              return;
            }

            if (!activeBillingRequestId) {
              console.log(
                "❌ No activeBillingRequestId - user cancelled early"
              );
              clearProcessingMessages();

              const cancelMessage = createMessage(
                "Your payment was not processed. Please try again...",
                false,
                { actionType: "info", options: ["Pay Now"] }
              );
              addMessage(cancelMessage);
              return;
            }

            // Show waiting message after exit
            const waitMessage = createMessage(
              "Please wait a minute while we verify your payment...",
              false,
              { actionType: "info" }
            );
            addMessage(waitMessage);

            setTimeout(async () => {
              console.log("Running check after onExit...");
              await checkPaymentFromTasks(waitMessage.id);
            }, 8000);

            setActiveBillingRequestId(null);
            hasTriggeredSuccess.current = false;
          },
        }
      : {
          billingRequestFlowID: "waiting-for-flow",
          environment: paymentEnvironment,
          onSuccess: () => {},
          onExit: () => {},
        }
  );

  // Add this new function to check payment status from tasks

  // Step 3: Payment — open the drop-in and poll tasks while active
  useEffect(() => {
    // Check if onboarding is complete in any message
    const onboardingComplete = messages.some(
      (msg) =>
        typeof msg.content === "string" &&
        msg.content.toLowerCase().includes("thank you!")
    );

    console.log(onboardingComplete, "onboardingComplete");

    if (activeFlowId && ready && !processingPayment && !onboardingComplete) {
      console.log("Opening GoCardless drop-in for flow:", activeFlowId);

      let intervalId;

      const timer = setTimeout(() => {
        open();

        // Start the interval for refetching
        intervalId = setInterval(async () => {
          // Check the current state of messages each time
          const currentSignFlow = messages.some(
            (msg) =>
              typeof msg.content === "string" &&
              msg.content.toLowerCase().includes("thank you!")
          );

          console.log(currentSignFlow, "currentSignFlow");

          // Stop refetching if sign flow is complete
          if (currentSignFlow) {
            console.log("Sign flow complete, stopping refetch");
            clearInterval(intervalId);
            return;
          }

          try {
            console.log("Refetching tasks...");
            const { error: refetchError } = await refetch();
            if (refetchError) {
              console.error("Refetch error:", refetchError);
            }
          } catch (err) {
            console.error("Error during refetching:", err);
          }
        }, 6000);
      }, 100);

      // Cleanup function
      return () => {
        clearTimeout(timer);
        if (intervalId) {
          clearInterval(intervalId);
        }
      };
    }
  }, [activeFlowId, ready, open, processingPayment, messages, refetch]);

  // Step 3: Payment — verify payment by inspecting tasks, with retries
  const checkPaymentFromTasks = async (waitMessageId, attempt = 1) => {
    console.log(`=== CHECKING PAYMENT FROM TASKS (Attempt ${attempt}) ===`);
    try {
      await queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      const { data: freshTasks, error: refetchError } = await refetch();

      if (refetchError) {
        console.error("❌ Failed to refetch tasks:", refetchError);
        throw new Error(`Failed to refetch tasks: ${refetchError.message}`);
      }

      const paymentTasks =
        freshTasks?.filter((task) => task.category === "payments") || [];
      const paymentCompleted = paymentTasks.some(
        (task) => task.status === "completed"
      );

      console.log("💳 Payment status check:", {
        paymentTasks: paymentTasks.map((t) => ({ id: t.id, status: t.status })),
        paymentCompleted,
      });

      if (paymentCompleted) {
        console.log("🎉 Payment completed! Showing onboarding complete");

        // Remove waiting message
        setMessages((prevMessages) =>
          prevMessages.filter((msg) => msg.id !== waitMessageId)
        );

        setPaymentComplete(true);
        setCurrentStage(VerificationStage.ONBOARDING_COMPLETE);

        setTimeout(() => {
          navigate("/submission-confirmation");
        }, 1500);
      } else {
        if (attempt < 10) {
          // Retry again in 4s
          console.log("⏳ Payment not completed yet, retrying...");
          setTimeout(
            () => checkPaymentFromTasks(waitMessageId, attempt + 1),
            4000
          );
          return;
        }

        // After retries → finally show failure
        console.log("❌ Payment not completed after retries, showing error");
        setMessages((prev) => prev.filter((msg) => msg.id !== waitMessageId));

        const retryMessage = createMessage(
          "⚠️ We couldn't verify your payment. Please try again.",
          false,
          { actionType: "info", options: ["Pay Now"] }
        );
        addMessage(retryMessage);
        setDisablePrompts(false);

        const tipMessage = createMessage(
          "💡 Tip: Check your bank details or try a different method.",
          false,
          { actionType: "complete" }
        );
        addMessage(tipMessage);
      }
    } catch (error) {
      console.error("❌ Error checking payment from tasks:", error);
      setMessages((prevMessages) =>
        prevMessages.filter((msg) => msg.id !== waitMessageId)
      );

      setDisablePrompts(false);
    } finally {
      setIsManuallyFetching(false);
    }
  };

  // =========================================
  // Step 3: Payment (GoCardless) — Flow Creation & Trigger
  // =========================================
  // GoCardless flow creation mutation
  const createGoCardlessFlow = useMutation<
    any,
    Error,
    { taskId: number; taskTitle?: string; }
  >({
    mutationFn: async (data) => {
      try {
        const res = await apiRequest("POST", "/api/gocardless/create-flow", {
          description: `Payment for task: ${data.taskTitle || data.taskId}`,
          taskId: data.taskId,
        });
        return await res.json();
      } catch (err: any) {
        let message = err?.message || "Failed to initialize payment";
        // Attempt to parse API error JSON from message like "400: {json}"
        try {
          const colonIndex = message.indexOf(":");
          const maybeJson = colonIndex >= 0 ? message.slice(colonIndex + 1).trim() : message;
          const parsed = JSON.parse(maybeJson);
          if (parsed && parsed.message) {
            message = parsed.message;
          }
        } catch {}
        throw new Error(message);
      }
    },
    onSuccess: (data) => {
      console.log("GoCardless flow created:", data);
      if (data?.payment_environment) {
        setPaymentEnvironment(data?.payment_environment);
      }
      if (data?.authorisation_url) {
        // setShowPaymentModal(true);
        setPaymentUrl(data?.authorisation_url);
      }

      if (!data.flow_id || !data.billing_request_id) {
        console.error("Missing flow ID or billing request ID in response");
        toast({
          title: "Payment Setup Error",
          description: "Could not initialize payment. Please try again.",
          variant: "destructive",
        });
        return;
      }

      setActiveFlowId(data.flow_id);
      setActiveBillingRequestId(data.billing_request_id);
    },
    onError: (error) => {
      console.error("GoCardless flow error:", error);
      toast({
        title: "Payment Setup Failed",
        description: getUserMessage(error.message || "Failed to initialize payment"),
        variant: "destructive",
      });
    },
  });

  // Handle payment click
  const handlePaymentClick = () => {
    if (!paymentTask) {
      toast({
        title: "No Payment Required",
        description: "No payment task found.",
        variant: "default",
      });
      return;
    }

    // Parse amount from task description if available
    const amountMatch = paymentTask.description?.match(/£(\d+(\.\d+)?)/);
    const amount = amountMatch ? parseFloat(amountMatch[1]) : 50; // Default to £50

    // Create GoCardless flow
    createGoCardlessFlow.mutate({
      taskId: paymentTask.id,
      taskTitle: paymentTask.title,
    });
  };

  // =========================================
  // Step 2: Signing (DocuSign) — Start, Verify, and Monitor
  // =========================================
  const callDocuSign = async () => {
    try {
      const response = await fetch("/api/docusign/sign", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          signerName: user?.firstName + " " + user?.lastName,
          signerEmail: user?.email,
          signerClientId: user?.id,
        }),
        credentials: "include",
      });
      console.log(response, "response");

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.message || "Verification failed");
      }
      setEnvelopeId(result.envelopeId);

      return {
        success: true,
        data: result,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || "An error occurred during verification",
      };
    }
  };

  // Step 2: Signing — auto-close timeouts for DocuSign and ID Scan modals
  useEffect(() => {
    if (isDocuSignModalOpen) {
      const timeoutId = setTimeout(() => {
        closeDocuSignModal();
      }, 5 * 60 * 1000);

      setDocuSignTimeout(timeoutId);
    } else {
      // Clear timeout when modal closes
      if (docuSignTimeout) {
        clearTimeout(docuSignTimeout);
        setDocuSignTimeout(null);
      }
    }
    if (showIDScanModal) {
      const timeoutId = setTimeout(() => {
        setShowIDScanModal(false);
        handleIDScanModalClose();
      }, 10 * 60 * 1000);

      setIdScanTimeout(timeoutId);
    } else {
      // Clear timeout when modal closes
      if (idScanTimeout) {
        clearTimeout(idScanTimeout);
        setIdScanTimeout(null);
      }
    }
  }, [isDocuSignModalOpen, showIDScanModal]);

  // Step 2: Signing — open DocuSign and handle redirect URL
  const handleDocuSignClick = async () => {
    setIsDocuSignLoading(true); // Start loading
    try {
      const response = await callDocuSign();

      if (!response?.success) {
        throw new Error(response?.error || "Unexpected error occurred.");
      }

      const redirectUrl = response?.data?.redirectUrl;

      if (!redirectUrl) {
        throw new Error("No redirect URL returned from DocuSign.");
      }

      setDocuSignUrl(redirectUrl);
      setIsDocuSignModalOpen(true); // loading ends when modal opens
    } catch (error) {
      const errorMessage = createMessage(
        "Unable to verify signature status. Please try again.",
        false,
        {
          actionType: "info",
          options: ["Try Again"],
        }
      );
      addMessage(errorMessage);
    } finally {
      setIsDocuSignLoading(false);
    }
  };
  const changeCountRef = useRef(0);
  // Step 2: Signing — close modal and verify status, then drive next steps
  const closeDocuSignModal = async () => {
    changeCountRef.current = 0;
    console.log("🔄 Closing DocuSign modal...");
    setDisablePrompts(false);
    setIsDocuSignModalOpen(false);
    setDocuSignUrl("");

    // IMMEDIATELY check pending signature count when modal closes
    const signatureCountResult = await checkUserSignatureCount(user.id);
    console.log("Signature count when modal closes:", signatureCountResult);

    // If user has 3 pending signatures, show law firm review message
    if (
      signatureCountResult.success &&
      signatureCountResult.hasPendingAllSignatures
    ) {
      try {
        const response = await fetch("/api/document/fail-signature", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({}),
        });

        if (!response.ok) {
          console.error("Failed to record verification attempt");
          return;
        }

        const reviewMessage = createMessage(
          "📬 Your documents are now being reviewed.",
          false,
          { actionType: "complete" }
        );
        addMessage(reviewMessage);

        const reviewTipMessage = createMessage(
          "💡 Tip: No need to retry — your law firm will follow up.",
          false,
          { actionType: "complete" }
        );
        addMessage(reviewTipMessage);
        return;
      } catch (error) {
        console.error("Error recording verification attempt:", error);
        return null;
      }
    }

    // Show "checking status" message instead of immediate success
    const checkingMessage = createMessage(
      "Checking your progress, Please wait...",
      false,
      { actionType: "info" }
    );
    addMessage(checkingMessage);

    console.log(envelopeId, "envelopeId for verification");

    try {
      // Check signature status from DocuSign
      const response = await fetch(`/docusign/verification/${envelopeId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      const docuSignResult = await response.json();
      console.log("📋 DocuSign verification result:", docuSignResult);

      // Check if signature is completed
      if (docuSignResult.envelope.status === "completed") {
        console.log("✅ DocuSign envelope completed successfully");

        // Set local state
        setSignatureComplete(true);

        // ===== CRITICAL FIX: Wait for DB update, then trigger flow directly =====
        console.log(
          "⏳ Waiting for database update, then manually fetching tasks..."
        );

        setTimeout(async () => {
          try {
            // setIsManuallyFetching(true);
            console.log("🔄 Refetching fresh tasks using React Query...");

            // Use React Query's refetch instead of manual fetch
            const { data: freshTasks, error: refetchError } = await refetch();

            if (refetchError) {
              console.error("❌ Failed to refetch tasks:", refetchError);
              throw new Error(
                `Failed to refetch tasks: ${refetchError.message}`
              );
            }

            console.log("📋 Fresh tasks from React Query:", freshTasks);

            // Filter signature tasks
            const signatureTasks =
              freshTasks?.filter((task) => task.category === "signature") || [];
            console.log(
              "📋 Fresh signature tasks:",
              signatureTasks.map((t) => ({
                id: t.id,
                title: t.title,
                status: t.status,
                completedAt: t.completedAt,
              }))
            );

            const signatureCompleted = signatureTasks.some(
              (task) => task.status === "completed"
            );
            console.log("✅ Signature completed check:", signatureCompleted);

            if (signatureCompleted) {
              console.log(
                "🎉 Signature tasks are completed! Processing payment flow..."
              );

              // Remove checking message first
              setMessages((prevMessages) =>
                prevMessages.filter((msg) => msg.id !== checkingMessage.id)
              );

              // Show success message
              const successMessage = createMessage(
                "📩 All documents signed and submitted.🚀 You're ready for the next step.",
                false,
                { actionType: "complete" }
              );
              addMessage(successMessage);
            } else {
              console.log("⚠️ Signature tasks still not completed");

              // Remove checking message and show retry
              setMessages((prevMessages) =>
                prevMessages.filter((msg) => msg.id !== checkingMessage.id)
              );

              const incompleteMessage = createMessage(
                "❌ We couldn't process your signature. ",
                false,
                {
                  actionType: "info",
                  options: ["Try Again"],
                }
              );
              addMessage(incompleteMessage);
              const tipMessage = createMessage(
                "💡 Tip: Check that all required fields are signed.",
                false,
                { actionType: "complete" }
              );
              addMessage(tipMessage);

              // Re-enable React Query for retry case
              setIsManuallyFetching(false);
            }
          } catch (error) {
            console.error("❌ Error refetching tasks:", error);

            // Remove checking message and show error
            setMessages((prevMessages) =>
              prevMessages.filter((msg) => msg.id !== checkingMessage.id)
            );

            const errorMessage = createMessage(
              "Unable to verify signature status. Please try again.",
              false,
              {
                actionType: "info",
                options: ["Try Again"],
              }
            );
            addMessage(errorMessage);

            // Re-enable React Query even on error
            setIsManuallyFetching(false);
          }
        }, 5000); // Wait 5 seconds for DocuSign success endpoint to update DB
      } else {
        console.log(
          "❌ DocuSign envelope not completed, status:",
          docuSignResult.envelope.status
        );

        // Remove checking message
        setMessages((prevMessages) =>
          prevMessages.filter((msg) => msg.id !== checkingMessage.id)
        );

        // Signature is NOT complete - show retry option
        setSignatureComplete(false);

        const incompleteMessage = createMessage(
          "❌ We couldn't process your signature.",
          false,
          {
            actionType: "info",
            options: ["Try Again"],
          }
        );
        addMessage(incompleteMessage);
        const tipMessage = createMessage(
          "💡 Tip: Check that all required fields are signed.",
          false,
          { actionType: "complete" }
        );
        addMessage(tipMessage);
      }
    } catch (error) {
      console.error("❌ Error checking signature status:", error);

      // Remove checking message on error
      setMessages((prevMessages) =>
        prevMessages.filter((msg) => msg.id !== checkingMessage.id)
      );

      // Show error message with retry option
      const errorMessage = createMessage(
        "Unable to verify signature status. Please try again.",
        false,
        {
          actionType: "info",
          options: ["Try Again"],
        }
      );
      addMessage(errorMessage);
    }
  };

  // Step 2: Signing — detect iframe navigations to infer completion
  const handleDocuSignIframeLoad = () => {
    if (docuSignIframeRef.current) {
      try {
        const iframe = docuSignIframeRef.current;
        const currentSrc = iframe.src;

        console.log(currentSrc, "currentSRC");

        if (currentSrc) {
          changeCountRef.current += 1; // Increment on each load
          console.log("Iframe change count:", changeCountRef.current);

          // ✅ Check for even number (2, 4, 6, ...)
          if (changeCountRef.current % 2 === 0) {
            setTimeout(() => {
              closeDocuSignModal();
            }, 5000);
          }
        }
      } catch (error) {
        console.log("Error checking iframe:", error);
      }
    }
  };

  // Step 2: Signing — monitor iframe URL periodically for success signal
  useEffect(() => {
    let intervalId;

    if (
      isDocuSignModalOpen &&
      !signatureComplete &&
      docuSignIframeRef.current
    ) {
      intervalId = setInterval(() => {
        try {
          const iframe = docuSignIframeRef.current;
          if (
            iframe &&
            iframe.src &&
            iframe.src.includes("/docusign/success")
          ) {
            closeDocuSignModal();
            clearInterval(intervalId);
          }
        } catch (error) {
          // Ignore errors
        }
      }, 2000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isDocuSignModalOpen, signatureComplete]);

  // =========================================
  // Step 5: Messaging & Chat Helpers
  // =========================================
  const handlePredefinedTopicClick = (topic) => {
    const answer = PREDEFINED_QA[topic];

    if (answer) {
      // Add user question message
      const userMessage = createMessage(topic, true);
      addMessage(userMessage);

      // Add bot answer message after a brief delay
      setTimeout(() => {
        const botMessage = createMessage(answer, false, {
          actionType: "info",
        });
        addMessage(botMessage);

        // Show current stage options again after answer
        setTimeout(() => {
          resumeCurrentStage();
        }, 1500);
      }, 500);

      return true; // Indicates this was a predefined topic
    }

    return false; // Not a predefined topic
  };

  const showBlockedMessage = async () => {
    const blockedMessage = createMessage(
      `Your information is currently being reviewed by your law firm. You will be contacted shortly.`.replace(
        "\n",
        "<br/> <br/>"
      ),
      false,
      {
        actionType: "error",
      }
    );

    addMessage(blockedMessage);

    // Disable further verification attempts

    setCurrentStage(VerificationStage.BLOCKED);
  };

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const scrollRef = useRef(null);

  // =========================================
  // Step 1: Verification (Onfido) — Status Checks & Transitions
  // =========================================
  const checkInitialVerificationStatus = async () => {
    setDisablePrompts(true);
    // Show loading message

    try {
      // Call the verification status endpoint (same as handleIDScanModalClose)
      const response = await fetch(
        `/api/get-workflowRun-id?workflowRunId=${workflowRunId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const result = await response.json();
      console.log("Verification status result:", result);
      setUserWorkFlowRunData(result.data.output);

      if (
        result.data.status === "declined" ||
        result.data.status === "review"
      ) {
        showBlockedMessage();
        // handleVerificationFailure(false);
      } else if (result.data.status === "processing") {
        startVerificationPolling({
          workflowRunId,
          addMessage,
          setMessages,
          setCurrentStage,
          createMessage,
          showBlockedMessage,
          setDisablePrompts,
        });
      } else {
        if (result.data.status === "approved") {
          // Call completion endpoint
          const completeResponse = await fetch(
            "/api/tasks/verify-profile/complete",
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
              },
              credentials: "include",
            }
          );

          // Show verification summary first
          let verificationSummary = `
          <div><strong>🔍 Verification Complete</strong></div>
          <hr style="margin: 6px 0;"/>
          <div><strong>✅ Summary:</strong> All checks passed successfully</div>
          <br>`;

          // Personal Information Section
          // verificationSummary += `<div><strong>👤 Personal Information:</strong></div>`;
          // if (result?.data?.output?.first_name || result?.data?.output?.last_name) {
          //   verificationSummary += `<div>• Name: ${
          //     result?.data?.output?.first_name || ""
          //   } ${result?.data?.output?.last_name || ""}</div>`;
          // }
          // if (result?.data?.output?.date_of_birth) {
          //   verificationSummary += `<div>• Date of Birth: ${new Date(
          //     result?.data?.output?.date_of_birth
          //   ).toLocaleDateString()}</div>`;
          // }
          // if (result?.data?.output?.gender) {
          //   verificationSummary += `<div>• Gender: ${result?.data?.output?.gender}</div>`;
          // }

          // // Address Information Section
          // if (result?.data?.output?.address_line_1) {
          //   verificationSummary += `<br><div><strong>🏠 Address Information:</strong></div>`;
          //   verificationSummary += `<div>• Address: ${result?.data?.output?.address_line_1}</div>`;
          // }

          // // Document Status Section
          // verificationSummary += `<br><div><strong>📄 Document Status:</strong></div>`;
          // if (result?.data?.output?.doc_overall) {
          //   verificationSummary += `<div>• Overall Status: ${
          //     result?.data?.output?.doc_overall.charAt(0).toUpperCase() +
          //     result?.data?.output?.doc_overall.slice(1)
          //   }</div>`;
          // }
          // if (result?.data?.output?.doc_sub) {
          //   verificationSummary += `<div>• Sub-document Status: ${
          //     result?.data?.output?.doc_sub.charAt(0).toUpperCase() +
          //     result?.data?.output?.doc_sub.slice(1)
          //   }</div>`;
          // }

          const summaryMessage = createMessage(verificationSummary, false, {
            actionType: "info",
          });
          addMessage(summaryMessage);

          if (completeResponse.ok) {
            // Wait 2 seconds then show success message
            setTimeout(() => {
              const successMessage = createMessage(
                "✅ Your ID has been verified. You're all set!\n🔒 Your information has been securely processed.",
                false,
                { actionType: "info" }
              );
              addMessage(successMessage);

              // Wait another 1 second then show signature prompt
              setTimeout(() => {
                setCurrentStage(VerificationStage.SIGNATURE_PENDING);

                const completionMessage = createMessage(
                  "✍️ Please sign your documents to continue: Terms of Business, Client Care Letter and more.",
                  false,
                  {
                    actionType: "complete",
                    options: ["Sign Documents"],
                  }
                );
                addMessage(completionMessage);
                setDisablePrompts(false);

                // Add tip message after a brief delay
                setTimeout(() => {
                  const tipMessage = createMessage(
                    "💡 Tip: Use your mouse or finger — all fields must be signed.",
                    false,
                    { actionType: "complete" }
                  );
                  addMessage(tipMessage);
                }, 500);
              }, 1000);
            }, 2000);
          }
        } else {
          const retryMessage = createMessage(
            "👋 Welcome back! We’ve saved your place. Pick up right where you left off." +
              result.data.status,
            false,
            {
              actionType: "info",
              options: ["Continue Verification"],
            }
          );
          addMessage(retryMessage);
          setCurrentStage(VerificationStage.VERIFICATION_PENDING);
          setDisablePrompts(false);
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  // UI: auto-scroll to bottom when messages change
  useEffect(() => {
    // document.body.style.overflow = 'hidden';
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth", // remove for instant scroll
      });
    }
  }, [messages]);

  // Step 1: Verification — fetch SDK token, initialize Onfido flow
  const fetchDataAndInitOnfido = async () => {
    setShowVerificationScreen(true);
    try {
      const response = await fetch("/api/get-sdk-token", {
        method: "GET",
        credentials: "include",
      });

      const data = await response.json();
      console.log(data, "response");
      setShowVerificationScreen(false);

      setOnFidoSrc(data.workflowRun.link.url);
      setOnfidoData(data);
      setShowIDScanModal(true);
      if (!data?.workflowRun?.sdk_token || !data?.workflowRun?.id) {
        console.error("Missing SDK token or workflow run ID from response");
        toast({
          title: "Verification Setup Error",
          description:  "Could not initialize verification. Please try again.",
          variant: "destructive",
        });
        return;
      }

      console.log(data.workflowRun.id, "data.workflowRun.id");
    } catch (error) {
      console.error("Error fetching SDK token:", error);
      toast({
        title: "Verification Setup Failed",
        description: "Failed to initialize verification. Please try again.",
        variant: "destructive",
      });
      // handleVerificationFailure(false);
    }
  };

  // =========================================
  // Step 4: Orchestration — Main tasks-driven stage manager
  // =========================================
  useEffect(() => {
    // Track run count across renders
    runCountRef.current += 1;
    console.log(`🔥 UseEffect run count: ${runCountRef.current}`);

    setShowPaymentModal(false);

    if (!user) {
      console.log("❌ No user, exiting useEffect");
      return;
    }

    if (flowProcessed) {
      console.log("⏭️ Flow already processed, skipping");
      return;
    }

    console.log("=== USEEFFECT RUNNING ===");
    console.log("Time:", new Date().toISOString());
    console.log("tasksLoading:", tasksLoading);
    console.log("tasks length:", tasks?.length);
    console.log("currentStage:", currentStage);

    if (tasksLoading && !userUsedAllAttemptsOfVerification) {
      console.log("📡 Still loading tasks...");
      const checkingMessage = createMessage(
        "Checking your progress, Please wait...",
        false,
        { actionType: "info" }
      );

      // ✅ Add message only if not already present
      if (!messages.some((m) => m.content === checkingMessage.content)) {
        addMessage(checkingMessage);
      }
      return;
    }

    if (tasksError) {
      console.error("❌ Tasks query error:", tasksError);
      return;
    }

    if (!tasks || !Array.isArray(tasks)) {
      console.log("❌ Invalid tasks data:", tasks);
      return;
    }

    console.log("📋 Processing tasks array with", tasks.length, "items");

    const {
      verificationCompleted,
      signatureCompleted,
      paymentCompleted,
      paymentProcessing,
    } = deriveOnboardingProgress(tasks);

    console.log("📊 Current Status Summary:");
    console.log("- verificationCompleted:", verificationCompleted);
    console.log("- signatureCompleted:", signatureCompleted);
    console.log("- paymentCompleted:", paymentCompleted);
    console.log("- paymentProcessing:", paymentProcessing);

    // Mark as processed
    setFlowProcessed(true);

    // ==================
    // VERIFICATION FLOW
    // ==================
    if (!verificationCompleted) {
      if (user.verificationBlockedAt) {
        showBlockedMessage();
      } else if (user.workFlowRunId) {
        checkInitialVerificationStatus();
        const tipMessage = createMessage(
          `👋 Hi ${user.firstName}, we noticed you didn't finish verifying your identity.`,
          false,
          { actionType: "continue-verification" }
        );
        if (!messages.some((m) => m.content === tipMessage.content)) {
          addMessage(tipMessage);
        }
      } else {
        const tipMessage = createMessage(
          `👋 Hi ${user.firstName}, we're ready to verify your identity.`,
          false,
          { actionType: "start-verification" }
        );
        if (!messages.some((m) => m.content === tipMessage.content)) {
          addMessage(tipMessage);
        }

        setShowVerificationScreen(true);
        fetchDataAndInitOnfido();
      }
    }

    // ==================
    // SIGNATURE FLOW
    // ==================
    else if (verificationCompleted && !signatureCompleted) {
      console.log("✍️ Need signatures - showing signature flow");

      checkUserSignatureCount(user.id)
        .then((signatureCountResult) => {
          if (
            signatureCountResult.success &&
            signatureCountResult.hasPendingAllSignatures
          ) {
            const reviewMessage = {
              id: generateMessageId("under-review"),
              content: "📬 Your documents are now being reviewed.",
              isUser: false,
              timestamp: new Date(),
              actionType: "complete",
              isDisabled: false,
            };
            setMessages([reviewMessage]);

            const tipMessage = createMessage(
              "💡 Tip: No need to retry — your law firm will follow up.",
              false,
              { actionType: "complete" }
            );
            if (!messages.some((m) => m.content === tipMessage.content)) {
              addMessage(tipMessage);
            }
          } else {
            const successMessage = createMessage(
              "✅ Your ID has been verified. You're all set!🔒 Your information has been securely processed.",
              false,
              { actionType: "info" }
            );
            if (!messages.some((m) => m.content === successMessage.content)) {
              addMessage(successMessage);
            }

            setCurrentStage(VerificationStage.SIGNATURE_PENDING);
            clearProcessingMessages();

            const signMsg = {
              id: generateMessageId("signature-pending"),
              content:
                "✍️ Please sign your documents to continue: Terms of Business, Client Care Letter and more.",
              isUser: false,
              timestamp: new Date(),
              actionType: "complete",
              options: ["Sign Documents"],
              isDisabled: false,
            };
            if (!messages.some((m) => m.content === signMsg.content)) {
              setMessages((prev) => [...prev, signMsg]);
            }

            const tipMessage = createMessage(
              "💡 Tip: Use your mouse or finger — all fields must be signed.",
              false,
              { actionType: "complete" }
            );
            if (!messages.some((m) => m.content === tipMessage.content)) {
              addMessage(tipMessage);
            }
          }
        })
        .catch((error) => {
          setCurrentStage(VerificationStage.SIGNATURE_PENDING);
          console.error("Error checking signature count:", error);

          const signMsg = {
            id: generateMessageId("signature-pending"),
            content:
              "✍️ Please sign your documents to continue: Terms of Business, Client Care Letter and more.",
            isUser: false,
            timestamp: new Date(),
            actionType: "complete",
            options: ["Sign Documents"],
            isDisabled: false,
          };
          if (!messages.some((m) => m.content === signMsg.content)) {
            setMessages([signMsg]);
          }

          const tipMessage = createMessage(
            "💡 Tip: Use your mouse or finger — all fields must be signed.",
            false,
            { actionType: "complete" }
          );
          if (!messages.some((m) => m.content === tipMessage.content)) {
            addMessage(tipMessage);
          }
        });
    }

    // ==================
    // PAYMENT FLOW
    // ==================
    else if (
      verificationCompleted &&
      signatureCompleted &&
      !paymentCompleted &&
      !processingPayment
    ) {
      console.log("💳 🎉 USEEFFECT PAYMENT FLOW TRIGGERED!");
      setCurrentStage(VerificationStage.PAYMENT_PENDING);

      const existingPaymentMessage = messages.find((msg) =>
        msg.content.includes("💳 Please authorise a one-time Direct Debit")
      );
      const existingSignFlow = messages.find((msg) =>
        msg.content.includes("Please sign your documents to continue")
      );

      if (!existingPaymentMessage) {
        setTimeout(() => {
          if (!existingSignFlow) {
            const onfidoMsg = createMessage(
              "✅ Your ID has been verified. You're all set!\n🔒 Your information has been securely processed.",
              false,
              { actionType: "info" }
            );
            if (!messages.some((m) => m.content === onfidoMsg.content)) {
              addMessage(onfidoMsg);
            }

            clearProcessingMessages();

            if (signatureRequired) {
              const successMessage = createMessage(
                "📩 All documents signed and submitted.🚀 You're ready for the next step.",
                false,
                { actionType: "complete" }
              );
              if (!messages.some((m) => m.content === successMessage.content)) {
                addMessage(successMessage);
              }
            }
          }

          const payMsg = isPaymentProcessing
            ? {
                id: generateMessageId("payment-processing"),
                content:
                  "💳 Payment is being processed. Please wait...",
                isUser: false,
                timestamp: new Date(),
                actionType: "info",
                isDisabled: true,
              }
            : {
                id: generateMessageId("payment-pending"),
                content:
                  "💳 Please authorise a one-time Direct Debit to complete onboarding.",
                isUser: false,
                timestamp: new Date(),
                actionType: "complete",
                options: ["Pay Now"],
                isDisabled: false,
              };
          if (!messages.some((m) => m.content === payMsg.content)) {
            setMessages((prev) => [...prev, payMsg]);
          }

          const tipMessage = createMessage(
            "🔐 Tip: It's secure and can be cancelled at any time.",
            false,
            { actionType: "complete" }
          );
          if (!messages.some((m) => m.content === tipMessage.content)) {
            addMessage(tipMessage);
          }
        }, 1000);
      }
    }

    // ==================
    // COMPLETION FLOW
    // ==================
    // else if ((verificationCompleted && signatureCompleted && paymentCompleted) || paymentSuccess) {
    else if (verificationCompleted && signatureCompleted && paymentCompleted) {
      setShowPaymentModal(false);
      console.log("🎉 All steps completed");

      setCurrentStage(VerificationStage.ONBOARDING_COMPLETE);

      if (!user.onboardingCompleted) {
        fetch("/api/onboarding/complete", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        });
      }

      navigate("/submission-confirmation");
    } else {
      console.log("❓ No matching condition in useEffect");
      console.log("Debug state:", {
        verificationCompleted,
        signatureCompleted,
        paymentCompleted,
        processingPayment,
        currentStage,
      });
    }
  }, [tasks]);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const paymentSuccess = params.get("payment_success");
      setPaymentSuccessFromUrl(paymentSuccess);
      if (paymentSuccess) {
        // Remove the query param to avoid repeated alerts on navigation
        params.delete("payment_success");
        const newQuery = params.toString();
        const newUrl = window.location.pathname + (newQuery ? `?${newQuery}` : "") + window.location.hash;
        window.history.replaceState({}, "", newUrl);
      }
    } catch (e) {
      // Ignore URL parsing errors
    }
  }, []);

  // Poll tasks every 2s when payment is processing and paymentSuccess flag present
  useEffect(() => {
    if (paymentSuccessFromUrl !== 'yes') return;

    let intervalId: number | undefined;

    const startPollingIfProcessing = async () => {
      try {
        const { data: freshTasks, error: refetchError } = await refetch();
        if (refetchError) {
          console.error("Polling init: failed to refetch tasks:", refetchError);
          return;
        }
        const paymentTasks = Array.isArray(freshTasks)
          ? freshTasks.filter((task: any) => task.category === "payments")
          : [];
        const processing = paymentTasks.some((t: any) => t.status === "processing");
        if (!processing) {
          // Only poll when a payment task is processing
          return;
        }

        const poll = async () => {
          try {
            const { data: fresh, error } = await refetch();
            if (error) {
              console.error("Polling: failed to refetch tasks:", error);
              return;
            }
            const payTasks = Array.isArray(fresh)
              ? fresh.filter((task: any) => task.category === "payments")
              : [];
            const paymentCompleted = payTasks.some((t: any) => t.status === "completed");
            if (paymentCompleted && intervalId !== undefined) {
              setShowCompletePaymentPopup(true);
              clearInterval(intervalId);
              intervalId = undefined;
              console.log("Polling stopped: payment task completed");
            }
          } catch (err) {
            console.error("Polling error:", err);
          }
        };

        // Start interval and run first check immediately
        await poll();
        intervalId = window.setInterval(poll, 2000);
      } catch (err) {
        console.error("Error starting payment polling:", err);
      }
    };

    startPollingIfProcessing();

    return () => {
      if (intervalId !== undefined) {
        clearInterval(intervalId);
      }
    };
  }, [paymentSuccessFromUrl, refetch]);

  // If payment already completed upon return, show completion popup immediately
  useEffect(() => {
    if (paymentSuccessFromUrl !== 'yes') return;
    if (!Array.isArray(tasks)) return;

    const list = tasks as Array<{ category: string; status: string }>;
    let paymentCompleted = false;
    for (const t of list) {
      if (t.category === "payments" && t.status === "completed") {
        paymentCompleted = true;
        break;
      }
    }
    if (paymentCompleted) {
      setShowCompletePaymentPopup(true);
    }
  }, [paymentSuccessFromUrl, tasks]);

  // Reset flowProcessed when tasks change
  useEffect(() => {
    if (tasks) {
      setFlowProcessed(false);
    }
  }, [tasks]);

  // Verification: handle ID Scan modal close and follow-up checks
  const handleIDScanModalClose = async () => {
    setShowIDScanModal(false);
    setIsResumeOnfidoFlow(false);

    // Show loading message
    const loadingMessage = createMessage(
      "Checking your progress, Please wait...",
      false,
      { actionType: "info" }
    );
    addMessage(loadingMessage);

    try {
      console.log(onFidoData, "onFidoData");
      console.log(user?.workFlowRunId, "user");

      // Hit the workflow endpoint
      const response = await fetch(
        `/api/get-workflowRun-id?workflowRunId=${workflowRunId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      const result = await response.json();
      console.log("Verification status result:", result);

      // Wait 3 seconds before checking the status
      await new Promise((resolve) => setTimeout(resolve, 3000));

      // Now check the status after the delay
      console.log("Checking status after 3 seconds...");

      setUserWorkFlowRunData(result.data.output);

      // Remove loading message
      setMessages((prevMessages) =>
        prevMessages.filter((msg) => msg.id !== loadingMessage.id)
      );

      // Check verification status
      if (result.data.status === "approved") {
        // Call completion endpoint
        const completeResponse = await fetch(
          "/api/tasks/verify-profile/complete",
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
          }
        );

        // Show verification summary first
        let verificationSummary = `
          <div><strong>🔍 Verification Complete</strong></div>
          <hr style="margin: 6px 0;"/>
          <div><strong>✅ Summary:</strong> All checks passed successfully</div>
          <br>`;

        // Personal Information Section
        // verificationSummary += `<div><strong>👤 Personal Information:</strong></div>`;
        // if (result?.data?.output?.first_name || result?.data?.output?.last_name) {
        //   verificationSummary += `<div>• Name: ${
        //     result?.data?.output?.first_name || ""
        //   } ${result?.data?.output?.last_name || ""}</div>`;
        // }
        // if (result?.data?.output?.date_of_birth) {
        //   verificationSummary += `<div>• Date of Birth: ${new Date(
        //     result?.data?.output?.date_of_birth
        //   ).toLocaleDateString()}</div>`;
        // }
        // if (result?.data?.output?.gender) {
        //   verificationSummary += `<div>• Gender: ${result?.data?.output?.gender}</div>`;
        // }

        // // Address Information Section
        // if (result?.data?.output?.address_line_1) {
        //   verificationSummary += `<br><div><strong>🏠 Address Information:</strong></div>`;
        //   verificationSummary += `<div>• Address: ${result?.data?.output?.address_line_1}</div>`;
        // }

        // // Document Status Section
        // verificationSummary += `<br><div><strong>📄 Document Status:</strong></div>`;
        // if (result?.data?.output?.doc_overall) {
        //   verificationSummary += `<div>• Overall Status: ${
        //     result?.data?.output?.doc_overall.charAt(0).toUpperCase() +
        //     result?.data?.output?.doc_overall.slice(1)
        //   }</div>`;
        // }
        // if (result?.data?.output?.doc_sub) {
        //   verificationSummary += `<div>• Sub-document Status: ${
        //     result?.data?.output?.doc_sub.charAt(0).toUpperCase() +
        //     result?.data?.output?.doc_sub.slice(1)
        //   }</div>`;
        // }

        const summaryMessage = createMessage(verificationSummary, false, {
          actionType: "info",
        });
        addMessage(summaryMessage);

        if (completeResponse.ok) {
          // Wait 2 seconds then show success message
          setTimeout(() => {
            const successMessage = createMessage(
              "✅ Your ID has been verified. You're all set!\n🔒 Your information has been securely processed.",
              false,
              { actionType: "info" }
            );
            addMessage(successMessage);

            // Wait another 1 second then show signature prompt
            setTimeout(() => {
              setCurrentStage(VerificationStage.SIGNATURE_PENDING);

              const completionMessage = createMessage(
                "✍️ Please sign your documents to continue: Terms of Business, Client Care Letter and more.",
                false,
                {
                  actionType: "complete",
                  options: ["Sign Documents"],
                }
              );
              addMessage(completionMessage);
              setDisablePrompts(false);

              // Add tip message after a brief delay
              setTimeout(() => {
                const tipMessage = createMessage(
                  "💡 Tip: Use your mouse or finger — all fields must be signed.",
                  false,
                  { actionType: "complete" }
                );
                addMessage(tipMessage);
              }, 500);
            }, 1000);
          }, 2000);
        }
      } else if (
        result.data.status === "declined" ||
        result.data.status === "review"
      ) {
        showBlockedMessage();
      } else if (result.data.status === "processing") {
        startVerificationPolling({
          workflowRunId,
          addMessage,
          setMessages,
          setCurrentStage,
          createMessage,
          showBlockedMessage,
          setDisablePrompts,
        });
      } else {
        const retryMessage = createMessage(
          "⚠️ Your ID verification is still pending. Please continue from where you left off.",
          false,
          {
            actionType: "info",
            options: ["Continue Verification"],
          }
        );
        setDisablePrompts(false);
        addMessage(retryMessage);
        setCurrentStage(VerificationStage.VERIFICATION_PENDING);
      }
    } catch (error) {
      console.error("Error in handleIDScanModalClose:", error);

      // Remove loading message in case of error
      setMessages((prevMessages) =>
        prevMessages.filter((msg) => msg.id !== loadingMessage.id)
      );
    }
  };

  // Messaging: push a message into chat, disabling previous actionable ones
  const addMessage = (message) => {
    if (!message.isUser) {
      disablePreviousMessages(true);
    }

    setMessages((prevMessages) => [...prevMessages, message]);
  };

  // Messaging: toggle previous actionable messages' interactivity
  const disablePreviousMessages = (shouldDisable) => {
    setMessages((prevMessages) => {
      const updatedMessages = prevMessages.map((message, index) => {
        if (index === prevMessages.length - 1 || message.isUser) {
          return message;
        }

        if (message.actionType === "reminder") {
          return message;
        }

        if (
          !message.isUser &&
          (message.options ||
            message.actionType === "upload" ||
            message.actionType === "form")
        ) {
          return { ...message, isDisabled: shouldDisable };
        }

        return message;
      });
      return updatedMessages;
    });
  };

  // Chat: submit handler and AI response integration
  const handleChatSubmit = async (e) => {
    e.preventDefault();
    if (!chatMessage.trim()) return;

    const userMessage = chatMessage.trim();
    setChatMessage("");

    // CHECK FOR PREDEFINED TOPICS FIRST - ADD THIS
    if (handlePredefinedTopicClick(userMessage)) {
      return; // Exit early if it was a predefined topic
    }

    // Rest of your existing handleChatSubmit logic stays the same...
    const userMsg = createMessage(userMessage, true);
    addMessage(userMsg);

    // Check if this is a manual message (not matching any predefined options)
    const isManualMessage = !isMessageMatchingOptions(
      userMessage,
      currentStage
    );

    if (isManualMessage) {
      // Add a typing indicator message
      const typingId = generateMessageId("typing");
      const typingMessage = {
        id: typingId,
        content: "...",
        isUser: false,
        timestamp: new Date(),
        actionType: "typing",
        isDisabled: false,
      };

      setMessages((prev) => [...prev, typingMessage]);

      // Process through OpenAI
      try {
        const aiChat = await processUserMessage(userMessage, currentStage, {
          selectedIDType,
          hasGeneratedSession,
          hasStartedVerification: showIDScanModal,
          verificationCompleted: verificationResult !== null,
          usingIDScanVerification: true,
        });

        // Remove typing indicator
        setMessages((prev) => prev.filter((m) => m.id !== typingId));

        if (aiChat && aiChat.success && aiChat.response) {
          // Add AI response to chat UI
          const aiMessage = createMessage(aiChat.response, false, {
            actionType: "info",
          });
          addMessage(aiMessage);
          resumeCurrentStage();
        } else {
          // Fallback message if AI response fails
          const fallbackMessage = createMessage(
            "I'm here to help with your verification process. Could you please be more specific about what you need assistance with?",
            false,
            { actionType: "info" }
          );
          addMessage(fallbackMessage);
          resumeCurrentStage();
        }
      } catch (error) {
        console.error("Error processing AI message:", error);
        // Error fallback message
        const errorMessage = createMessage(
          "I'm having trouble processing your message right now. Please try again or continue with your verification process.",
          false,
          { actionType: "info" }
        );
        addMessage(errorMessage);
        resumeCurrentStage();
      }
      return;
    }

    // Rest of your existing handleChatSubmit logic...
    if (stageBeingProcessed.current !== null) {
      return;
    }

    stageBeingProcessed.current = currentStage;
    setLastActivity(new Date());
  };

  // Chat: match free-text to stage options
  const isMessageMatchingOptions = (message, stage) => {
    const normalizedMessage = message.toLowerCase().trim();

    switch (stage) {
      case VerificationStage.WELCOME:
        return suggestedTopics.some(
          (topic) => topic.toLowerCase() === normalizedMessage
        );

      case VerificationStage.PROOF_OF_FUNDS:
        return [
          "bank statement",
          "savings account",
          "investment portfolio",
          "gift letter",
          "skip",
        ].includes(normalizedMessage);

      default:
        return false;
    }
  };

  // Messaging: resume current stage by re-surfacing the right prompts
  const resumeCurrentStage = () => {
    console.log(currentStage, "CurrentStage");
    setTimeout(() => {
      if (currentStage === "Signature Pending") {
        const signatureMessage = createMessage(
          "✍️ Please sign your documents to continue: Terms of Business, Client Care Letter and more.",
          false,
          {
            actionType: "complete",
            options: ["Sign Documents"],
            isDisabled: false,
          }
        );
        addMessage(signatureMessage);
        const tipMessage = createMessage(
          "💡 Tip: Use your mouse or finger — all fields must be signed.",
          false,
          { actionType: "complete" }
        );
        addMessage(tipMessage);
      } else if (currentStage === "verification_pending") {
        const retryMessage = createMessage(
          "⚠️ Your ID verification is still pending. Please continue from where you left off.",
          false,
          {
            actionType: "info",
            options: ["Continue Verification"],
          }
        );
        setDisablePrompts(false);
        addMessage(retryMessage);
      } else if (currentStage === "Signature Blocked") {
        const reviewMessage = createMessage(
          "📬 Your documents are now being reviewed.",
          false,
          {
            actionType: "complete",
            isDisabled: false,
          }
        );
        addMessage(reviewMessage);
        const tipMessage = createMessage(
          "💡 Tip: No need to retry — your law firm will follow up.",
          false,
          { actionType: "complete" }
        );
        addMessage(tipMessage);
      } else if (currentStage === "Payment Pending") {
        setCurrentStage(VerificationStage.PAYMENT_PENDING);
        const msg = isPaymentProcessing
          ? {
              id: generateMessageId("payment-processing"),
              content: "💳 Payment is being processed. Please wait...",
              isUser: false,
              timestamp: new Date(),
              actionType: "info",
              isDisabled: true,
            }
          : {
              id: generateMessageId("payment-pending"),
              content:
                "💳 Please authorise a one-time Direct Debit to complete onboarding.",
              isUser: false,
              timestamp: new Date(),
              actionType: "complete",
              options: ["Pay Now"],
              isDisabled: false,
            };
        setMessages((prev) => [...prev, msg]);
        const tipMessage = createMessage(
          "🔐 Tip: It's secure and can be cancelled at any time.",
          false,
          { actionType: "complete" }
        );
        addMessage(tipMessage);
      } else if (currentStage === "Payment completed") {
        navigate("/submission-confirmation");
      } else if (currentStage === "change_check") {
        const changeCheckMessage = createMessage(
          "✏️ Would you like to update your name, address, or bank details?",
          false,
          {
            actionType: "info",
            options: ["Yes, there are changes"],
          }
        );
        addMessage(changeCheckMessage);
        const tipMessage = createMessage(
          " 💡 Tip: If anything differs from your ID, update it now to avoid delays.",
          false,
          { actionType: "complete" }
        );
        addMessage(tipMessage);
      } else if (currentStage === "modify_details") {
        const modifyFormId = generateMessageId("modify-form");
        const modifyFormMessage = createMessage(
          "Please update your information as needed:",
          false,
          {
            actionType: "form",
            formId: modifyFormId,
          }
        );
        addMessage(modifyFormMessage);
        setShowModifyForm(true);
      } else if (currentStage === "proof_of_funds") {
        const formId = generateMessageId("funds-form");
        const bankingDetailsFormMessage = createMessage(
          "we need your banking details to verify your source of funds.",
          false,
          {
            actionType: "form",
            formId: formId,
          }
        );
        addMessage(bankingDetailsFormMessage);
      } else if (currentStage === "verification_failed") {
        const retryMessage = createMessage(
          "⚠️ We couldn’t verify your ID.",
          false,
          {
            actionType: "info",
            options: ["Retry Verification"],
          }
        );
        addMessage(retryMessage);
        const tipMessage = createMessage(
          "💡 Tip: Make sure your image is clear, all details are visible, and there’s no glare.",
          false,
          { actionType: "complete" }
        );
        addMessage(tipMessage);
      } else if (currentStage === "blocked") {
        const blockedMessage = createMessage(
          `Your information is currently being reviewed by your law firm. You will be contacted shortly.
        .`.replace("\n", "<br/> <br/>"),
          false,
          {
            actionType: "error",
          }
        );

        addMessage(blockedMessage);
      }
      setDisablePrompts(false);
    }, 1200);
  };

  // Messaging: handle option button selections
  const handleOptionClick = (option) => {
    console.log(option, "option");

    if (option === "Continue Verification") {
      setIsResumeOnfidoFlow(true);
      setShowIDScanModal(true);
    }
    if (option === "Check Status") {
      const userMessage = createMessage(
        "🔄 Checking verification status...",
        true
      );
      addMessage(userMessage);

      // Call the same API again
      setTimeout(async () => {
        await checkInitialVerificationStatus(); // reuse existing logic
      }, 500);
    }

    if (option === "Try Now") {
      fetchDataAndInitOnfido();
    }

    if (option === "Sign Documents") {
      setDisablePrompts(true);
      // Update the button to show loading immediately
      setMessages((prevMessages) =>
        prevMessages.map((message) => {
          if (message.options?.includes("Sign Documents")) {
            return {
              ...message,
              options: ["Sign Documents"],
              isDisabled: true,
            };
          }
          return message;
        })
      );

      handleDocuSignClick();
      return;
    }

    // Handle DocuSign retry specifically
    if (option === "Try Again") {
      // Check if this is a DocuSign retry (when signature is not complete)
      if (!signatureComplete) {
        const retryMessage = createMessage("I want to try signing again", true);
        addMessage(retryMessage);

        const processingMessage = createMessage(
          "Setting up document signing process again...",
          false,
          { actionType: "info" }
        );
        addMessage(processingMessage);

        // Retry DocuSign after short delay
        setTimeout(() => {
          handleDocuSignClick();
        }, 1000);
        return;
      }
      return;
    }

    if (option === "Pay Now") {
      setDisablePrompts(true);
      const userMessage = createMessage("I want to pay now", true);
      addMessage(userMessage);

      // Show processing message
      const processingMessage = createMessage(
        "⚙️ Setting up your payment... please wait.",
        false,
        { actionType: "info" }
      );
      addMessage(processingMessage);
      const tipMessage = createMessage(
        "💡 Tip: Don’t close this screen until it finishes.",
        false,
        { actionType: "complete" }
      );
      addMessage(tipMessage);

      // Handle payment
      setTimeout(() => {
        handlePaymentClick();
      }, 1000);
      return;
    }

    if (stageBeingProcessed.current !== null) {
      return;
    }

    const selectionMessage = createMessage(`I select: ${option}`, true);
    addMessage(selectionMessage);

    // Set stageBeingProcessed only for other options
    stageBeingProcessed.current = currentStage;
    if (currentStage === VerificationStage.REVIEW_DETAILS) {
      if (option === "Submit") {
        const verificationSuccessMessage = createMessage(
          "🎉 You’ve completed verification.📨 We’ll guide you through what’s next.",
          false,
          { actionType: "info" }
        );
        addMessage(verificationSuccessMessage);

        setTimeout(() => {
          setCurrentStage(VerificationStage.SIGNATURE_PENDING);
          const completionMessage = createMessage(
            "✍️ Please sign your documents to continue: Terms of Business, Client Care Letter and more.",
            false,
            {
              actionType: "complete",
              options: ["Sign Documents"],
            }
          );

          addMessage(completionMessage);
          const tipMessage = createMessage(
            "💡 Tip: Use your mouse or finger — all fields must be signed.",
            false,
            { actionType: "complete" }
          );
          addMessage(tipMessage);
        }, 1000);
        // setLocation("/onboarding");
        return;
      }
    }
  };

  // These functions can be removed as they're no longer needed with IDScan
  const handleFileUpload = () => {};

  return (
    <>
      <div className="flex flex-col md:flex-row bg-white h-[100vh] overflow-hidden">
        {isDocuSignModalOpen ||
        showIDScanModal ||
        showBankingForm ||
        showPaymentModal ||
        showCompletePaymentPopup ||
        showModifyForm ? (
          <></>
        ) : (
          <div className="fixed w-full" style={{ zIndex: "99" }}>
            <MobileNav />
          </div>
        )}

        {showVerificationScreen ? (
          <VerificationStartScreen />
        ) : (
          <main
            className="flex-1 flex flex-col bg-white"
            style={{ height: "100vh" }}
          >
            {showCompletePaymentPopup && (
              <SuccessPopup
                onClose={() => setShowCompletePaymentPopup(false)}
              />
            )}
            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto overflow-x-hidden"
              style={{ paddingBottom: "0px" }}
            >
              <div className="mobile-content md:mt-[18px] mt-[40px] md:max-w-3xl md:mx-auto px-4 pt-6 bg-white">
                <Card className=" shadow-none border-0">
                  <OnboardingHeader messages={messages} />
                  {!showCompletePaymentPopup && (
                    <CardContent className="p-3 shadow-none border-0">
                      <div className="space-y-4 mb-4">
                        {messages.map((message, index) => (
                          <ChatMessage
                            key={`msg-${message.id}-${index}`}
                            message={message}
                            isMobile={isMobile}
                            selectedIDType={selectedIDType}
                            currentStage={currentStage}
                            onOptionClick={handleOptionClick}
                            onFileUpload={handleFileUpload}
                            onStartCamera={() => {}}
                            setShowBankingForm={setShowBankingForm}
                          />
                        ))}
                      </div>
                    </CardContent>
                  )}
                </Card>
                {/* Dynamic bottom padding based on input height */}
                <div
                  className={`${
                    messages.length > 1 ? "pb-[8rem]" : "pb-[8rem]"
                  }`}
                ></div>
              </div>
            </div>

            {/* Fixed Bottom Area - Chat Input and Warning */}
            {!showCompletePaymentPopup && (
              <div className="fixed bottom-0 left-0 right-0 bg-white z-10">
                <div className="md:max-w-3xl md:mx-auto px-4 py-3 ">
                  <ChatInput
                    chatMessage={chatMessage}
                    setChatMessage={setChatMessage}
                    onSubmit={handleChatSubmit}
                    currentStage={currentStage}
                    stageBeingProcessed={stageBeingProcessed}
                    suggestedTopics={suggestedTopics}
                    isProcessing={isProcessing}
                    onPredefinedTopicClick={handlePredefinedTopicClick}
                    consentGiven={consentGiven}
                    disablePrompt={disablePrompts}
                  />
                  <OnboardingWarning />
                </div>
              </div>
            )}

            {/* Modals remain outside the flex structure */}
            <IDScanModal
              isOpen={showIDScanModal}
              onClose={handleIDScanModalClose}
              sessionUrl={onFidoSrc}
              onFidoData={onFidoData}
              isResumeOnfidoFlow={isResumeOnfidoFlow}
              user={user}
              createMessage={createMessage}
              addMessage={addMessage}
            />

            {/* DocuSign Modal */}
            <DocuSignModal
              isOpen={isDocuSignModalOpen}
              onClose={closeDocuSignModal}
              docuSignUrl={docuSignUrl}
              docuSignIframeRef={docuSignIframeRef}
              handleDocuSignIframeLoad={handleDocuSignIframeLoad}
            />
          </main>
        )}
        <NeedHelp />
      </div>
    </>
  );
}
